import type { ReactNode } from "react";
import { useEffect } from "react";

interface Props {
    children: ReactNode;
}

/**
 * GlobalPasteHandler - Handles paste events globally across the application
 * Trims leading and trailing whitespace when pasting into input fields
 */
const GlobalPasteHandler = ({ children }: Props) => {
    useEffect(() => {
        const handlePaste = (event: ClipboardEvent) => {
            // Get the active element to check if we're in an input field
            const activeElement = document.activeElement;

            // Only handle HTMLInputElement and HTMLTextAreaElement (not contentEditable)
            if (
                !(
                    activeElement instanceof HTMLInputElement ||
                    activeElement instanceof HTMLTextAreaElement
                )
            ) {
                return;
            }

            // Get pasted text
            const pastedText = event.clipboardData?.getData("text");
            if (!pastedText) {
                return;
            }

            // Trim leading and trailing whitespace
            const trimmedText = pastedText.trim();

            // Only modify if trimming actually changed the text
            if (trimmedText !== pastedText) {
                event.preventDefault();

                // Use execCommand for simple, reliable paste
                document.execCommand("insertText", false, trimmedText);
            }
        };

        // Add paste event listener
        document.addEventListener("paste", handlePaste);

        // Cleanup
        return () => {
            document.removeEventListener("paste", handlePaste);
        };
    }, []);

    return <>{children}</>;
};

export default GlobalPasteHandler;
